import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { dateSplitter } from "@/utils/formatting/deteSplitter";
import { useParams } from "react-router-dom";

export interface LateRecord {
  id?: string;
  studentId: string;
  date: string;
  lateType:
    | "morning_lineup"
    | "after_lineup"
    | "period_1"
    | "period_2"
    | "period_3"
    | "period_4"
    | "period_5"
    | "period_6"
    | "period_7";
  period?: string;
  time?: string;
  school_id?: string;
  recorded_at?: string;
  recorded_by?: string;
}

export const useLateAttendance = (
  startDate?: string,
  endDate?: string,
  grade: string = "all",
  section: string = "all"
) => {
  const queryClient = useQueryClient();
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const { schoolId: schooIdParam } = useParams();
  const schoolId = schooIdParam || userProfile?.school_id;

  // ---------------------------
  // FETCH FUNCTION
  // ---------------------------
  const fetchLateRecordsFn = async () => {
    if (!schoolId) return [];

    let query = supabase
      .from("late_attendance_records")
      .select(
        `*, students!student_id (
          id, grade, section
        )`
      )
      .eq("school_id", schoolId)
      .order("date", { ascending: false });

    if (startDate) query = query.gte("date", startDate);
    if (endDate) query = query.lte("date", endDate);

    const { data, error } = await query;
    if (error) throw error;

    let temp = data;

    if (section !== "all") {
      temp = temp.filter((r) => r.students?.section === section);
    }
    if (grade !== "all") {
      temp = temp.filter((r) => r.students?.grade === grade);
    }

    return temp.map((record) => ({
      id: record.id,
      studentId: record.student_id,
      date: record.date,
      lateType: record.late_type,
      period: record.period?.toString(),
      school_id: record.school_id,
      recorded_at: record.recorded_at,
      recorded_by: record.recorded_by,
    })) as LateRecord[];
  };

  // ---------------------------
  // QUERY: LOAD LATE RECORDS
  // ---------------------------
  const { data: lateRecords = [], isLoading: loading } = useQuery({
    queryKey: ["late_records", schoolId, startDate, endDate, grade, section],
    queryFn: fetchLateRecordsFn,
    enabled: !!schoolId,
  });

  // Expose fetch function with same API as before
  const fetchLateRecords = () =>
    queryClient.invalidateQueries({
      queryKey: ["late_records", schoolId, startDate, endDate, grade, section],
    });

  // ---------------------------
  // MUTATION: RECORD LATE ATTENDANCE
  // ---------------------------
  const recordMutation = useMutation({
    mutationFn: async ({
      studentId,
      date,
      lateType,
      period,
    }: {
      studentId: string;
      date: string;
      lateType: LateRecord["lateType"];
      period?: string;
    }) => {
      const recordData = {
        school_id: schoolId,
        student_id: studentId,
        date,
        late_type: lateType,
        period: period ? parseInt(period) : null,
        recorded_by: user?.id,
      };

      const { data, error } = await supabase
        .from("late_attendance_records")
        .upsert(recordData, {
          onConflict: "school_id,student_id,date,period",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => fetchLateRecords(),
  });

  const recordLateAttendance = (
    studentId: string,
    date: string,
    lateType: LateRecord["lateType"],
    period?: string
  ) => recordMutation.mutate({ studentId, date, lateType, period });

  // ---------------------------
  // MUTATION: REMOVE LATE RECORD
  // ---------------------------
  const removeMutation = useMutation({
    mutationFn: async ({
      studentId,
      date,
      period,
    }: {
      studentId: string;
      date: string;
      period?: string;
    }) => {
      let query = supabase
        .from("late_attendance_records")
        .delete()
        .eq("school_id", schoolId)
        .eq("student_id", studentId)
        .eq("date", date);

      if (period) query = query.eq("period", parseInt(period));
      else query = query.is("period", null);

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => fetchLateRecords(),
  });

  const removeLateRecord = (studentId: string, date: string, period?: string) =>
    removeMutation.mutate({ studentId, date, period });

  // ---------------------------
  // HELPER FUNCTIONS (same API)
  // ---------------------------
  const getStudentLateRecord = (
    studentId: string,
    date: string,
    period?: string
  ) =>
    lateRecords.find(
      (record) => record.studentId === studentId && record.date === date
    );

  const getLateRecordsForDate = (date: string) =>
    lateRecords.filter((r) => r.date === date);

  const getStudentLateCount = (studentId: string) =>
    lateRecords.filter((r) => r.studentId === studentId).length;

  const getTodayLateCount = () => {
    const today = dateSplitter(new Date());
    return lateRecords.filter((record) => record.date === today).length;
  };

  return {
    lateRecords,
    loading,
    fetchLateRecords,
    recordLateAttendance,
    removeLateRecord,
    getStudentLateRecord,
    getLateRecordsForDate,
    getStudentLateCount,
    getTodayLateCount,
  };
};
